import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'

import { textColor } from '@ui-kit/styles/colors'

const textBase = tv({
  extend: textColor,
  variants: {
    ...textColor.variants,
    emphasis: {
      default: '',
      medium: 'opacity-70',
      low: 'opacity-40',
    },
    align: {
      left: 'text-left',
      right: 'text-right',
      center: 'text-center',
    },
    type: {
      primary: 'uppercase',
      secondary: '',
    },
  },
  defaultVariants: { emphasis: 'default', align: 'left', type: 'secondary' },
})

const headingBase = tv({
  extend: textBase,
  variants: {
    ...textBase.variants,
    type: {
      primary: 'font-adieu-bold',
      secondary: 'font-inter-semibold',
    },
  },
  defaultVariants: { type: 'secondary' },
})
const regularTextBase = tv({
  extend: textBase,
  base: 'font-inter-regular',
  variants: {
    bold: {
      true: 'font-inter-medium',
    },
    weight: {
      regular: 'font-inter-regular',
      semibold: 'font-inter-semibold',
      bold: 'font-inter-medium',
    },
  },
})

export const h1 = tv({
  extend: headingBase,
  variants: {
    type: {
      primary: 'text-5xl leading-14 md:text-8xl lg:text-10xl lg:leading-18',
      secondary: 'text-6xl leading-13 tracking-[-0.4px] lg:text-9xl lg:leading-16',
    },
  },
})

export const h2 = tv({
  extend: headingBase,
  variants: {
    type: {
      primary: 'text-2xl md:text-6xl md:leading-14 lg:text-9xl lg:leading-16',
      secondary: 'text-2xl leading-10 tracking-[-0.4px] lg:text-6xl lg:leading-13',
    },
  },
})

export const h3 = tv({
  extend: headingBase,
  variants: {
    type: {
      primary: 'text-xl md:text-3xl lg:text-6xl',
      secondary: 'text-xl leading-11 lg:text-2xl lg:leading-10',
    },
  },
  defaultVariants: { emphasis: 'default', align: 'left', type: 'secondary' },
})

export const h4 = tv({
  extend: textBase,
  variants: {
    type: {
      primary: 'font-adieu-bold text-base md:text-lg md:leading-11 lg:text-xl lg:leading-13',
      secondary: 'font-inter-semibold text-base leading-8 lg:text-xl lg:leading-11',
    },
  },
})

export const h5 = tv({
  extend: headingBase,
  variants: {
    type: {
      primary: 'text-sm leading-6 md:text-base md:leading-7 lg:leading-9',
      secondary: 'text-sm leading-6 lg:text-xl lg:leading-11',
    },
  },
})

export const body1 = tv({
  extend: regularTextBase,
  base: 'text-base leading-8',
})

export const body2 = tv({
  extend: regularTextBase,
  base: 'text-sm leading-6',
})

export const caption = tv({
  extend: regularTextBase,
  base: 'text-xs leading-5',
})

export const overline = tv({
  extend: textBase,
  base: 'font-inter-medium text-2xs leading-4',
})

export const link = 'underline text-black'

export const buttonLabel = tv({
  extend: regularTextBase,
  base: 'line-clamp-1 font-inter-semibold text-sm leading-6',
})
export type BaseTextProps = VariantProps<typeof textBase>
export type RegularTextProps = VariantProps<typeof regularTextBase>
export type HeadingProps = VariantProps<typeof headingBase>
